import { useState, useEffect } from 'react';
import { getSessionStats } from '../api/sessions';
import { useAuth } from '../context/AuthContext';
import AlertBanner from '../components/common/AlertBanner';
import LoadingSpinner from '../components/common/LoadingSpinner';

const RANGES = [
  { weeks: 4, label: '4 Wochen' },
  { weeks: 12, label: '12 Wochen' },
  { weeks: 52, label: '1 Jahr' },
];

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('de-DE') : '–';
}

function Stars({ value }) {
  if (value == null) return <span className="text-muted">–</span>;
  return (
    <span className="text-warning text-nowrap" title={`${value} von 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i
          key={n}
          className={`bi ${value >= n ? 'bi-star-fill' : value >= n - 0.5 ? 'bi-star-half' : 'bi-star'}`}
        />
      ))}
    </span>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <div className="col-6 col-lg-3">
      <div className="card h-100">
        <div className="card-body">
          <div className="text-muted small mb-1"><i className={`bi ${icon} me-1`} />{label}</div>
          <div className="fs-3 fw-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function WeekChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const dense = data.length > 16;
  return (
    <div className="d-flex align-items-end gap-1" style={{ height: 160 }}>
      {data.map((d) => (
        <div
          key={d.week_start}
          className="flex-fill d-flex flex-column align-items-center justify-content-end h-100"
          title={`Woche ab ${formatDate(d.week_start)}: ${d.count} Einheit(en)`}
        >
          {!dense && d.count > 0 && <div className="small text-muted">{d.count}</div>}
          <div
            className="w-100 rounded-top"
            style={{
              height: `${(d.count / max) * 100}%`,
              minHeight: d.count ? 4 : 1,
              background: d.count ? '#f97316' : 'var(--bs-border-color)',
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default function ProgressPage() {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState(12);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const isParticipant = user?.role === 'participant';

  useEffect(() => {
    setError('');
    getSessionStats(weeks)
      .then(setStats)
      .catch((e) => setError(e.message || 'Statistik konnte nicht geladen werden.'));
  }, [weeks]);

  if (!stats && !error) return <LoadingSpinner />;

  const totals = stats?.totals;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center pb-2 mb-4 border-bottom flex-wrap gap-2">
        <h1 className="h2 mb-0">
          <i className="bi bi-graph-up me-2" />
          Fortschritt
        </h1>
        <div className="btn-group btn-group-sm">
          {RANGES.map((r) => (
            <button
              key={r.weeks}
              type="button"
              className={`btn ${weeks === r.weeks ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setWeeks(r.weeks)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />}

      {stats && (
        <>
          <p className="text-muted small">
            {isParticipant ? 'Deine abgeschlossenen Trainingseinheiten.' : 'Alle abgeschlossenen Trainingseinheiten deines Studios.'}
          </p>

          <div className="row g-3 mb-4">
            <StatTile icon="bi-check2-circle" label="Einheiten" value={totals.sessions} />
            <StatTile icon="bi-clock" label="Trainingszeit" value={formatDuration(totals.total_seconds)} />
            <StatTile
              icon="bi-star"
              label="Ø Bewertung"
              value={totals.avg_rating != null ? totals.avg_rating.toLocaleString('de-DE') : '–'}
            />
            <StatTile
              icon="bi-fire"
              label="Serie"
              value={`${totals.streak_weeks} ${totals.streak_weeks === 1 ? 'Woche' : 'Wochen'}`}
            />
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h2 className="h6 mb-3">Einheiten pro Woche</h2>
              <WeekChart data={stats.per_week} />
              <div className="d-flex justify-content-between small text-muted mt-1">
                <span>{formatDate(stats.per_week[0]?.week_start)}</span>
                <span>diese Woche</span>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-xl-7">
              <div className="card h-100">
                <div className="card-body">
                  <h2 className="h6 mb-3">Workouts</h2>
                  {stats.workouts.length === 0 ? (
                    <div className="text-muted small">Im gewählten Zeitraum wurde noch kein Workout abgeschlossen.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Workout</th>
                            <th className="text-end">Einheiten</th>
                            <th>Ø Bewertung</th>
                            <th className="text-end">Ø Anstrengung</th>
                            <th className="text-end">Zuletzt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.workouts.map((w) => (
                            <tr key={w.workout_id}>
                              <td>{w.name}</td>
                              <td className="text-end">{w.sessions}</td>
                              <td>
                                <Stars value={w.avg_rating} />
                                {w.feedback_count > 0 && <span className="text-muted small ms-1">({w.feedback_count})</span>}
                              </td>
                              <td className="text-end">{w.avg_rpe != null ? `${w.avg_rpe.toLocaleString('de-DE')}/10` : '–'}</td>
                              <td className="text-end">{formatDate(w.last_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-xl-5">
              <div className="card h-100">
                <div className="card-body">
                  <h2 className="h6 mb-3">Letzte Rückmeldungen</h2>
                  {stats.recent_feedback.length === 0 ? (
                    <div className="text-muted small">
                      Noch keine Rückmeldungen. Nach jedem Workout kann im Presenter eine Bewertung abgegeben werden.
                    </div>
                  ) : (
                    <ul className="list-unstyled mb-0">
                      {stats.recent_feedback.map((f) => (
                        <li key={f.session_id} className="border-bottom py-2">
                          <div className="d-flex justify-content-between gap-2">
                            <strong className="text-truncate">{f.workout_name}</strong>
                            <Stars value={f.rating} />
                          </div>
                          <div className="small text-muted">
                            {formatDate(f.date)}
                            {!isParticipant && f.user_name && ` · ${f.user_name}`}
                            {f.rpe && ` · Anstrengung ${f.rpe}/10`}
                          </div>
                          {f.comment && <div className="small mt-1">„{f.comment}"</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
